// src/pages/Manufacturing/ManufacturingRecord/components/ManufacturingRecordTable.js
import React, { useState } from "react";
import { Table, Space, Popconfirm, message, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import { formatToCompanyTimezone } from "../../../../lib/functions";
import { useNavigate } from "react-router-dom";
import { deleteManufacturingRecord } from "../../Manufacturing Entry/manufacturingThunk";
import { fetchManufacturingRecord } from "../ManufacturingRecordThunk";
import { useIsAdminUser } from "../../../../lib/accessList";
import { useTxLock } from "../../../../hooks/useTxLock";
import VoucherPdfActions from "../../../../components/common/VoucherPdfActions";
import "../../../../components/common/bt.css";

const ManufacturingRecordTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector(
    (state) => state.manufacturingRecord,
  );

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const money = (v) => Number(v || 0).toFixed(2);
  const isAdminUser = useIsAdminUser();
  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  // -----------------------
  // Columns for WITHOUT ITEM (summary)
  // -----------------------
  const summaryColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Created By", dataIndex: "createdBy" },
    {
      title: "Labour Cost",
      dataIndex: "labour",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Others Cost",
      dataIndex: "others",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Total Cost",
      dataIndex: "totalCost",
      render: (val) => Number(val).toFixed(2),
    },
    // { title: "Paid", dataIndex: "paid", render: (val) => Number(val).toFixed(2) },
    // { title: "Due", dataIndex: "due", render: (val) => Number(val).toFixed(2) },
  !printMode && {
  title: "Actions",
  className: "no-print",
  render: (_, record) => {

    const txLocked = isTxLocked(record?.date);

    return (
      <Space>
        {/* View */}
        <Tooltip title="View Voucher">
          <EyeOutlined
            style={{ color: "#1677ff", cursor: "pointer" }}
            onClick={() =>
              navigate(`/manufacturing-quick-voucher/${record.id}`)
            }
          />
        </Tooltip>

        {/* Edit */}
        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <EditOutlined
            style={{
              marginRight: 16,
              cursor:
                (isAdminUser || canEdit) && !txLocked
                  ? "pointer"
                  : "not-allowed",
              color:
                (isAdminUser || canEdit) && !txLocked
                  ? "#1677ff"
                  : "#ccc",
            }}
            onClick={(e) => {
              if (!(isAdminUser || canEdit) || txLocked) return;

              e.stopPropagation();

              navigate(`/manufacturing/${record.id}?tab=entry`);
            }}
          />
        </Tooltip>

        {/* Delete */}
        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Popconfirm
            title="Delete Manufacturing Entry?"
            description="This will revert stock & cannot be undone"
            okText="Yes"
            cancelText="No"
            disabled={!(isAdminUser || canDelete) || txLocked}
            onConfirm={async () => {
              if (!(isAdminUser || canDelete) || txLocked) return;

              await dispatch(deleteManufacturingRecord(record.id)).unwrap();

              message.success("Manufacturing record deleted");

              dispatch(
                fetchManufacturingRecord({
                  selectedFilterType: filters.filterType,
                  selectedRecordType: filters.recordType,
                  filters,
                })
              );
            }}
          >
            <DeleteOutlined
              style={{
                cursor:
                  (isAdminUser || canDelete) && !txLocked
                    ? "pointer"
                    : "not-allowed",
                color:
                  (isAdminUser || canDelete) && !txLocked
                    ? "#ff4d4f"
                    : "#ccc",
              }}
            />
          </Popconfirm>
        </Tooltip>

        <VoucherPdfActions
          url={`/print/manufacturing/${record.id}`}
          fileName={`Manufacturing_${record.vch_no || record.id}.pdf`}
        />
      </Space>
    );
  },
},
  ].filter(Boolean);

  // -----------------------
  // Columns for WITH ITEM (detailed)
  // -----------------------
  const withItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Created By", dataIndex: "createdBy" },
    { title: "Item", dataIndex: "item" },
    { title: "Rate", dataIndex: "rate" },
    ...(list.some((row) => row.discount && row.discount !== "0%")
      ? [{ title: "Discount%", dataIndex: "discount" }]
      : []),
    ...(list.some((row) => row.vat && row.vat !== "0%")
      ? [{ title: "VAT%", dataIndex: "vat" }]
      : []),
    { title: "Qty", dataIndex: "qty" },
    { title: "Total", dataIndex: "total" },
    !printMode && {
      title: "Actions",
      className: "no-print",
      render: (_, record) => (
        <Space>
          {/* View */}
          <EyeOutlined
            style={{ color: "#1677ff", cursor: "pointer" }}
            onClick={() =>
              navigate(`/manufacturing-quick-voucher/${record.id}`)
            }
          />

          {/* Edit */}

          <EditOutlined
            style={{ color: "#52c41a", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/manufacturing/${record.id}?tab=entry`);
            }}
          />


          {/* Delete */}

          <Popconfirm
            title="Delete Manufacturing Entry?"
            description="This will revert stock & cannot be undone"
            okText="Yes"
            cancelText="No"
            disabled={!isAdminUser || !canDelete}
            onConfirm={async () => {
              await dispatch(deleteManufacturingRecord(record.id)).unwrap();
              message.success("Manufacturing record deleted");
              dispatch(
                fetchManufacturingRecord({
                  selectedFilterType: filters.filterType,
                  selectedRecordType: filters.recordType,
                  filters,
                }),
              );
            }}
          >
            <DeleteOutlined
              disabled={!isAdminUser || !canDelete}
              style={{
                cursor: (isAdminUser || canDelete) ? "pointer" : "not-allowed",
                color: (isAdminUser || canDelete) ? "#ff4d4f" : "#ccc",
              }}

            />
          </Popconfirm>

          <VoucherPdfActions
            url={`/print/manufacturing/${record.id}`}
            fileName={`Manufacturing_${record.vch_no || record.id}.pdf`}
          />

        </Space>
      ),
    },
  ].filter(Boolean);

  // -----------------------
  // Grand Totals
  // -----------------------
  let totals;
  if (filters.recordType === "with item") {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        acc.master_qty += Number(row.masterQty || 0);
        acc.base_qty += Number(row.baseQty || 0);
        return acc;
      },
      { total: 0, master_qty: 0, base_qty: 0 },
    );
  } else {
    totals = list.reduce(
      (acc, row) => {
        acc.labour += Number(row.labour || 0);
        acc.others += Number(row.others || 0);
        acc.totalCost += Number(row.totalCost || 0);
        acc.paid += Number(row.paid || 0);
        acc.due += Number(row.due || 0);
        return acc;
      },
      { labour: 0, others: 0, totalCost: 0, paid: 0, due: 0 },
    );
  }

  // -----------------------
  // Expandable rows (for WITH ITEM)
  // -----------------------
  const expandable =
    filters.recordType === "with item"
      ? {
        expandedRowRender: (record) => (
          <div style={{ fontSize: "12px", color: "#555" }}>
            {record.unitDetails}
          </div>
        ),
        rowExpandable: (record) => !!record.unitDetails,
      }
      : undefined;

  if (printMode) {
    const isWithItem = filters.recordType === "with item";
    const showDiscount = list.some((row) => row.discount && row.discount !== "0%");
    const showVat = list.some((row) => row.vat && row.vat !== "0%");
    const right = { textAlign: "right" };

    return (
      <table className="ledger-print-table">
        <colgroup>
          {isWithItem ? (
            <>
              <col style={{ width: "3%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "21%" }} />
              <col style={{ width: "9%" }} />
              {showDiscount && <col style={{ width: "8%" }} />}
              {showVat && <col style={{ width: "8%" }} />}
              <col style={{ width: "9%" }} />
              <col style={{ width: "10%" }} />
            </>
          ) : (
            <>
              <col style={{ width: "4%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "21%" }} />
            </>
          )}
        </colgroup>
        <tbody>
          <tr className="ledger-print-heading">
            <td>No.</td>
            <td>Voucher No</td>
            <td>Date</td>
            <td>Created By</td>
            {isWithItem ? (
              <>
                <td>Item</td>
                <td style={right}>Rate</td>
                {showDiscount && <td style={right}>Discount%</td>}
                {showVat && <td style={right}>VAT%</td>}
                <td style={right}>Qty</td>
                <td style={right}>Total</td>
              </>
            ) : (
              <>
                <td style={right}>Labour Cost</td>
                <td style={right}>Others Cost</td>
                <td style={right}>Total Cost</td>
              </>
            )}
          </tr>

          {list.map((row, index) => (
            <tr key={row.id || index}>
              <td>{index + 1}</td>
              <td>{row.vch_no}</td>
              <td>{row.date ? formatToCompanyTimezone(row.date).split(" ")[0] : "-"}</td>
              <td>{row.createdBy}</td>
              {isWithItem ? (
                <>
                  <td>{row.item}</td>
                  <td style={right}>{row.rate}</td>
                  {showDiscount && <td style={right}>{row.discount}</td>}
                  {showVat && <td style={right}>{row.vat}</td>}
                  <td style={right}>{row.qty}</td>
                  <td style={right}>{row.total}</td>
                </>
              ) : (
                <>
                  <td style={right}>{money(row.labour)}</td>
                  <td style={right}>{money(row.others)}</td>
                  <td style={right}>{money(row.totalCost)}</td>
                </>
              )}
            </tr>
          ))}

          <tr className="ledger-total-row">
            {isWithItem ? (
              <>
                <td
                  colSpan={
                    7 +
                    (showDiscount ? 1 : 0) +
                    (showVat ? 1 : 0)
                  }
                  style={right}
                >
                  Grand Total : Master U. Qty = {totals.master_qty}, Base U. Qty = {totals.base_qty}
                </td>
                <td style={right}>{totals.total.toFixed(2)}</td>
              </>
            ) : (
              <>
                <td colSpan={4} style={right}>Grand Total</td>
                <td style={right}>{totals.labour.toFixed(2)}</td>
                <td style={right}>{totals.others.toFixed(2)}</td>
                <td style={right}>{totals.totalCost.toFixed(2)}</td>
              </>
            )}
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <Table
      rowKey="id"
      data-testid="manufacturing-record-table"
      columns={
        filters.recordType === "with item" ? withItemColumns : summaryColumns
      }
      dataSource={list}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      expandable={expandable}
      scroll={printMode ? undefined : { x: "max-content" }}
      size={printMode ? "small" : "middle"}
      summary={() => {
        if (filters.recordType === "with item") {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={9}>
                <strong>
                  Grand Total : Master U. Qty = {totals.master_qty}, Base U. Qty
                  = {totals.base_qty}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10}>
                <strong>{totals.total.toFixed(2)}</strong>
              </Table.Summary.Cell>
              {!printMode && (
                <Table.Summary.Cell index={10}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        } else {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}></Table.Summary.Cell>
              <Table.Summary.Cell index={3}>
                {totals.labour.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4}>
                {totals.others.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                {totals.totalCost.toFixed(2)}
              </Table.Summary.Cell>
              {/* <Table.Summary.Cell index={6}>{totals.paid.toFixed(2)}</Table.Summary.Cell>
              <Table.Summary.Cell index={7}>{totals.due.toFixed(2)}</Table.Summary.Cell> */}
              {!printMode && (
                <Table.Summary.Cell index={8}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        }
      }}
    />
  );
};

export default ManufacturingRecordTable;
